import { AppIcon } from '@/components/ui/app-icon';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { emptyStateIcon } from '@/components/ui/empty-state-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { SettingsLayout } from '@/containers/settings/settings-layout.component';
import {
  SETTINGS_PANE,
  SETTINGS_PANE_ROWS,
  SETTINGS_PANE_SECTION,
} from '@/containers/settings/settings-pane';
import {
  SettingsPaneHeader,
  SettingsPaneRow,
} from '@/containers/settings/settings-pane-layout.component';
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from '@/store/features/notifications.feature';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { iconCheck, iconTriangleExclamation } from '@/lib/icons';

const LoadFailedGlyph = emptyStateIcon(iconTriangleExclamation);

/** The preferences this page switches, by the key the API takes. */
type PreferenceKey =
  | 'drip_emails'
  | 'email_low_credits'
  | 'in_app_notifications';

/**
 * A change shown before the preferences say so. `savedAt` is set once the
 * save came back: from then on it waits for a read that agrees.
 */
interface PendingChange {
  value: boolean;
  savedAt: number | null;
}

export const Route = createFileRoute('/settings/notifications')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    unsubscribe: (search.unsubscribe as string) || undefined,
  }),
});

function RouteComponent() {
  const { unsubscribe } = useSearch({ from: '/settings/notifications' });
  const {
    data: prefs,
    isLoading,
    isError,
    isFetching,
    fulfilledTimeStamp,
    refetch,
  } = useGetNotificationPreferencesQuery();
  const [updatePrefs] = useUpdateNotificationPreferencesMutation();
  // A change shown at once and held until the saved preferences say the same,
  // so the switch does not flip back for the read that follows the save.
  const [pending, setPending] = useState<
    Partial<Record<PreferenceKey, PendingChange>>
  >({});
  // A saved change is let go once the preferences agree with it, or, when
  // the server settled on another value, once the read after the save is in.
  useEffect(() => {
    if (isFetching) return;
    setPending((current) => {
      const next = { ...current };
      let changed = false;
      for (const key of Object.keys(current) as PreferenceKey[]) {
        const change = current[key];
        if (!change || change.savedAt === null) continue;
        if (
          prefs?.[key] === change.value ||
          (fulfilledTimeStamp ?? 0) > change.savedAt
        ) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [prefs, isFetching, fulfilledTimeStamp, pending]);

  const valueOf = (key: PreferenceKey): boolean =>
    pending[key]?.value ?? prefs?.[key] ?? false;
  const busy = Object.values(pending).some(
    (change) => change?.savedAt === null || !isError
  );

  const change = useCallback(
    async (key: PreferenceKey, value: boolean) => {
      setPending((current) => ({
        ...current,
        [key]: { value, savedAt: null },
      }));
      try {
        const saved = await updatePrefs({ [key]: value }).unwrap();
        setPending((current) => ({
          ...current,
          [key]: {
            value: saved?.preferences?.[key] ?? value,
            savedAt: Date.now(),
          },
        }));
      } catch {
        toast.error('Failed to update preference. Please try again.');
        setPending((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      }
    },
    [updatePrefs]
  );

  // Unsubscribe once when arriving from the ?unsubscribe=drip email link. It
  // goes through the same held change as the switch, so the page shows the
  // saved answer and does not send it again while the read after the save
  // still has the old value.
  const linkUnsubscribed = useRef(false);
  useEffect(() => {
    if (unsubscribe !== 'drip' || !prefs?.drip_emails) return;
    if (linkUnsubscribed.current) return;
    linkUnsubscribed.current = true;
    void change('drip_emails', false);
  }, [unsubscribe, prefs?.drip_emails, change]);

  const shell = (children: ReactNode) => (
    <>
      <Head heading='Notification Settings' />
      <AuthCheck>
        <SettingsLayout>{children}</SettingsLayout>
      </AuthCheck>
    </>
  );

  if (!prefs && isError) {
    return shell(
      <div className={SETTINGS_PANE} role='alert'>
        <SettingsPaneHeader title='Notifications' hideTitle />
        <EmptyState
          icon={LoadFailedGlyph}
          headline="Couldn't load your notifications"
          action={
            <Button
              variant='outline'
              size='sm'
              className='h-8 rounded-md text-xs'
              onClick={() => void refetch()}
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (isLoading || !prefs) {
    return shell(
      <div className={SETTINGS_PANE} aria-busy='true'>
        <SettingsPaneHeader title='Notifications' hideTitle />
        <div className={SETTINGS_PANE_SECTION}>
          <div className='space-y-3 pt-1'>
            <Skeleton className='h-9 w-full rounded-lg' />
            <Skeleton className='h-9 w-full rounded-lg' />
            <Skeleton className='h-9 w-full rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  // Drip unsubscribe banner - shown when arriving from an email link
  const dripBanner = unsubscribe === 'drip' && !valueOf('drip_emails') && (
    <div
      role='status'
      className='flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-900/10'
    >
      <AppIcon
        icon={iconCheck}
        className='shrink-0 text-emerald-600 dark:text-emerald-400'
        style={{ fontSize: 20 }}
      />
      <p className='text-sm font-medium text-emerald-800 dark:text-emerald-300'>
        Unsubscribed from onboarding emails
      </p>
    </div>
  );
  const threshold = prefs.email_threshold ?? 20;

  // A pane beside the settings nav: the rows flat on the pane, each
  // preference a label and a switch. The nav names the pane, so its title
  // is for screen readers only and no section title repeats it.
  return shell(
    <div className={SETTINGS_PANE}>
      <SettingsPaneHeader title='Notifications' hideTitle />
      {dripBanner}
      <div
        className={`${SETTINGS_PANE_SECTION} first-of-type:border-t-0 first-of-type:pt-0`}
      >
        <div className={SETTINGS_PANE_ROWS}>
          <SettingsPaneRow label='Onboarding emails'>
            <Switch
              aria-label='Onboarding emails'
              checked={valueOf('drip_emails')}
              disabled={busy}
              onCheckedChange={(checked) => void change('drip_emails', checked)}
            />
          </SettingsPaneRow>
          <SettingsPaneRow
            label='Low credit warnings'
            description={`Below ${threshold}%`}
          >
            <Switch
              aria-label='Low credit warnings'
              checked={valueOf('email_low_credits')}
              disabled={busy}
              onCheckedChange={(checked) =>
                void change('email_low_credits', checked)
              }
            />
          </SettingsPaneRow>
          <SettingsPaneRow label='In-app notifications'>
            <Switch
              aria-label='In-app notifications'
              checked={valueOf('in_app_notifications')}
              disabled={busy}
              onCheckedChange={(checked) =>
                void change('in_app_notifications', checked)
              }
            />
          </SettingsPaneRow>
        </div>
      </div>
    </div>
  );
}
